import { useEffect, useState } from "react";
import useSWR, { useSWRConfig } from "swr";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { readStaleSessionCache, writeSessionCache } from "@/lib/sessionCache";
import { SkeletonTable } from "@/components/Skeletons";
import PageLayout from "@/components/PageLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import FileDropZone from "@/components/FileDropZone";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Search, CreditCard, Users, Euro, TrendingUp, TrendingDown, Edit, Trash2, Upload, FileText, AlertCircle, AlertTriangle, Mail, DollarSign, Phone, Copy, ExternalLink, Info, Link2, CheckCircle2 } from "lucide-react";
import { useYear } from "@/contexts/YearContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Template email
const EMAIL_TEMPLATES = {
  sollecito_pagamento: {
    subject: "Promemoria pagamento – pratica in corso",
    body: `Gentile Cliente,

con la presente desideriamo ricordarLe che, alla data odierna, il pagamento relativo alla Sua pratica risulta ancora non pervenuto.

Il saldo è necessario per garantire:
• la continuità della fornitura
• il rispetto delle tempistiche concordate
• la corretta pianificazione delle attività operative

Qualora il pagamento fosse già stato effettuato, La preghiamo di considerare questa comunicazione come nulla. In caso contrario, restiamo in attesa di riscontro.

Cordiali saluti
Showroom Ingenio`
  },
  ultimo_sollecito: {
    subject: "Ultimo sollecito pagamento – sospensione attività",
    body: `Gentile Cliente,

facendo seguito alle precedenti comunicazioni, ad oggi il pagamento relativo alla Sua pratica risulta ancora insoluto.

In assenza di riscontro entro breve termine, saremo costretti a sospendere le attività operative collegate all'intervento, come previsto dagli accordi sottoscritti.

La invitiamo pertanto a regolarizzare la posizione o a contattarci tempestivamente per evitare blocchi o rinvii.

In attesa di un Suo cortese riscontro.
Cordiali saluti
Showroom Ingenio`
  },
  richiesta_contatto: {
    subject: "Richiesta di contatto – pratica in gestione",
    body: `Gentile Cliente,

La contattiamo perché avremmo necessità di un confronto diretto in merito alla Sua pratica.

La invitiamo a:
• rispondere a questa email indicando un recapito telefonico
oppure
• contattarci direttamente negli orari di apertura

Un breve confronto ci permetterà di procedere in modo corretto e senza fraintendimenti.

Restiamo in attesa.
Cordiali saluti
Showroom Ingenio`
  }
};

export default function PaymentsPage({ user, logout }) {
  const { selectedYear } = useYear();
  const _cached = readStaleSessionCache(`payments_${selectedYear}`);
  const { mutate } = useSWRConfig();

  // SWR keys — usati anche dal prefetch al login per pre-popolare la cache
  const paymentsKey = `/payments?year=${selectedYear}`;
  const statsKey = `/payments/stats?year=${selectedYear}`;
  const morosiKey = `/payments/morosi?year=${selectedYear}`;
  const interviewsKey = `/interviews?year=${selectedYear}&light=true`;
  const commesseKey = `/commesse-pose?year=${selectedYear}&light=true`;

  // Fetch iniziale via SWR (cache in-memory + persister localStorage)
  const { data: swrPayments } = useSWR(paymentsKey, { fallbackData: _cached?.payments });
  const { data: swrStats } = useSWR(statsKey, { fallbackData: _cached?.stats });
  const { data: swrMorosi } = useSWR(morosiKey, { fallbackData: _cached?.morosi });
  const { data: swrInterviews } = useSWR(interviewsKey);
  const { data: swrCommesse } = useSWR(commesseKey, { fallbackData: _cached?.commesse });

  const [payments, setPayments] = useState(swrPayments || _cached?.payments || []);
  const [morosi, setMorosi] = useState(swrMorosi || _cached?.morosi || []);
  const [commesse, setCommesse] = useState(swrCommesse || _cached?.commesse || []);
  const [activeTab, setActiveTab] = useState("tutti"); // "tutti" | "morosi"
  const [stats, setStats] = useState(swrStats || _cached?.stats || { totale_clienti: 0, totale_contratti: 0, totale_incassato: 0, totale_da_incassare: 0, totale_scaduto: 0 });
  const [loading, setLoading] = useState(!_cached && !swrPayments);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [interviews, setInterviews] = useState([]);
  // Ricerca intervista nel dialog "Nuovo Cliente"
  const [interviewSearchTerm, setInterviewSearchTerm] = useState("");
  const [selectedInterview, setSelectedInterview] = useState("");
  const [gmailConnected, setGmailConnected] = useState(false);
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    nome: "",
    cognome: "",
    telefono: "",
    email: "",
    indirizzo: "",
    citta: "",
    piano_consegna: "",
    importo_contratto: "",
    numero_tranche: 1,
    note: ""
  });

  // Sincronizza i dati SWR (freschi in background) verso gli state locali
  useEffect(() => { if (swrPayments) setPayments(swrPayments); }, [swrPayments]);
  useEffect(() => { if (swrStats) setStats(swrStats); }, [swrStats]);
  useEffect(() => { if (swrMorosi) setMorosi(swrMorosi); }, [swrMorosi]);
  useEffect(() => { if (swrInterviews) setInterviews(swrInterviews); }, [swrInterviews]);
  useEffect(() => { if (swrCommesse) setCommesse(swrCommesse); }, [swrCommesse]);
  useEffect(() => {
    if (swrPayments && loading) setLoading(false);
  }, [swrPayments, loading]);

  // Fetch data — usato post-mutazione. Invalida tutte le cache SWR + gmail status one-shot.
  const fetchData = async () => {
    try {
      const gmailStatusRes = await axios.get(`${API}/oauth/gmail/status`, { withCredentials: true }).catch(() => ({ data: { connected: false } }));
      setGmailConnected(gmailStatusRes.data.connected);
      // Revalida tutti gli endpoint SWR
      await Promise.all([
        mutate(paymentsKey),
        mutate(statsKey),
        mutate(morosiKey),
        mutate(interviewsKey),
        mutate(commesseKey),
      ]);
      // Mantengo scrittura anche su sessionCache legacy per retro-compat
      // (i dati aggiornati vengono passati da SWR agli state via useEffect sopra)
      writeSessionCache(`payments_${selectedYear}`, {
        payments,
        stats,
        morosi,
        commesse,
      });
    } catch (e) {
      console.error("Error refreshing payments:", e);
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  // Gestisci i parametri URL dal callback Gmail
  useEffect(() => {
    if (searchParams.get("gmail_connected") === "true") {
      toast.success("Gmail collegato con successo!");
      setGmailConnected(true);
      // Pulisci l'URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    if (searchParams.get("gmail_error")) {
      toast.error(`Errore connessione Gmail: ${searchParams.get("gmail_error")}`);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [searchParams]);

  const handleConnectGmail = async () => {
    try {
      const res = await axios.get(`${API}/oauth/gmail/login`, { withCredentials: true });
      // Reindirizza alla pagina di autorizzazione Google
      window.location.href = res.data.authorization_url;
    } catch (e) {
      console.error("Error initiating Gmail OAuth:", e);
      toast.error("Errore nella connessione a Gmail");
    }
  };

  const handleDisconnectGmail = async () => {
    if (!window.confirm("Sei sicuro di voler disconnettere Gmail?")) return;
    try {
      await axios.delete(`${API}/oauth/gmail/disconnect`, { withCredentials: true });
      setGmailConnected(false);
      toast.success("Gmail disconnesso");
    } catch (e) {
      console.error("Error disconnecting Gmail:", e);
      toast.error("Errore nella disconnessione");
    }
  };

  const copyEmailToClipboard = (templateKey) => {
    const template = EMAIL_TEMPLATES[templateKey];
    const emailText = `Oggetto: ${template.subject}\n\n${template.body}`;
    
    navigator.clipboard.writeText(emailText).then(() => {
      toast.success("Email copiata negli appunti!");
    }).catch(() => {
      toast.error("Errore nella copia");
    });
  };

  useEffect(() => {
    // Gmail status: fetch one-shot al mount / cambio anno (non parte di SWR)
    axios.get(`${API}/oauth/gmail/status`, { withCredentials: true })
      .then((r) => setGmailConnected(r.data.connected))
      .catch(() => setGmailConnected(false));
  }, [selectedYear]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        importo_contratto: parseFloat(formData.importo_contratto)
      };
      if (editingPayment) {
        await axios.put(`${API}/payments/${editingPayment.id}`, data, { withCredentials: true });
        toast.success("Pagamento aggiornato");
      } else {
        await axios.post(`${API}/payments`, data, { withCredentials: true });
        toast.success("Pagamento creato");
      }
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (e) {
      console.error("Error saving payment:", e);
      toast.error("Errore nel salvataggio");
    }
  };

  const handleTrancheUpdate = async (paymentId, trancheNum, trancheData) => {
    try {
      const updateData = { [`tranche_${trancheNum}`]: trancheData };
      await axios.put(`${API}/payments/${paymentId}`, updateData, { withCredentials: true });
      toast.success("Tranche aggiornata");
      fetchData();
    } catch (e) {
      console.error("Error updating tranche:", e);
      toast.error("Errore nell'aggiornamento");
    }
  };

  const handleUploadContabile = async (paymentId, trancheNum, file) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      await axios.post(`${API}/payments/${paymentId}/upload-contabile/${trancheNum}`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Contabile caricata");
      fetchData();
    } catch (e) {
      console.error("Error uploading contabile:", e);
      toast.error("Errore nel caricamento");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo pagamento?")) return;
    try {
      await axios.delete(`${API}/payments/${id}`, { withCredentials: true });
      toast.success("Pagamento eliminato");
      fetchData();
    } catch (e) {
      console.error("Error deleting payment:", e);
      toast.error("Errore nell'eliminazione");
    }
  };

  const openEditDialog = (payment) => {
    setEditingPayment(payment);
    setFormData({
      nome: payment.nome,
      cognome: payment.cognome,
      telefono: payment.telefono || "",
      email: payment.email || "",
      indirizzo: payment.indirizzo || "",
      citta: payment.citta || "",
      piano_consegna: payment.piano_consegna || "",
      importo_contratto: payment.importo_contratto.toString(),
      numero_tranche: payment.numero_tranche,
      note: payment.note || ""
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingPayment(null);
    setSelectedInterview("");
    setFormData({
      nome: "",
      cognome: "",
      telefono: "",
      email: "",
      indirizzo: "",
      citta: "",
      piano_consegna: "",
      importo_contratto: "",
      numero_tranche: 1,
      note: ""
    });
  };

  const handleImportFromInterview = (interviewId) => {
    setSelectedInterview(interviewId);
    if (!interviewId) {
      // Reset se deselezionato
      return;
    }
    const interview = interviews.find(i => i.id === interviewId);
    if (interview) {
      setFormData({
        ...formData,
        nome: interview.nome || "",
        cognome: interview.cognome || "",
        telefono: interview.telefono || "",
        email: interview.email || "",
        indirizzo: `${interview.via || ""} ${interview.civico || ""}`.trim(),
        citta: interview.citta || "",
        piano_consegna: interview.piano || "",
        note: interview.note || ""
      });
      toast.success("Dati importati dall'intervista");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value || 0);
  };

  const getPaymentStatus = (payment) => {
    let totalePagato = 0;
    let hasOverdue = false;
    const today = new Date();

    for (let i = 1; i <= payment.numero_tranche; i++) {
      const tranche = payment[`tranche_${i}`];
      if (tranche) {
        if (tranche.pagata) {
          totalePagato += tranche.importo_ivato || 0;
        } else {
          if (tranche.data_scadenza && new Date(tranche.data_scadenza) < today) {
            hasOverdue = true;
          }
        }
      }
    }

    // Calcola "Da Pagare" come differenza tra importo contratto e totale pagato
    const totaleDaPagare = Math.max(0, (payment.importo_contratto || 0) - totalePagato);

    return { totalePagato, totaleDaPagare, hasOverdue };
  };

  const filteredPayments = payments.filter((p) =>
    `${p.nome} ${p.cognome}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Trova la commessa collegata a un cliente per nome e cognome
  const findCommessaForClient = (nome, cognome) => {
    return commesse.find(c => 
      c.cliente_nome?.toLowerCase() === nome?.toLowerCase() && 
      c.cliente_cognome?.toLowerCase() === cognome?.toLowerCase()
    );
  };

  return (
    <PageLayout user={user} logout={logout} testId="payments-page" className="overflow-auto">
        <div className="p-8 max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Scadenzario Pagamenti</h1>
              <p className="text-slate-600 mt-1">Gestisci i pagamenti dei clienti</p>
            </div>
            <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-[#1e40af] hover:bg-[#1e3a8a] text-white" data-testid="add-payment-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Nuovo Cliente
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingPayment ? "Modifica Pagamento" : "Nuovo Cliente"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  {/* Importa da Intervista */}
                  {!editingPayment && interviews.length > 0 && (
                    <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                      <Label className="text-blue-800 font-medium">Importa dati da Intervista</Label>
                      <Input
                        type="text"
                        placeholder="Cerca per cognome, nome, codice o telefono..."
                        value={interviewSearchTerm}
                        onChange={(e) => setInterviewSearchTerm(e.target.value)}
                        className="mt-2 bg-white"
                        data-testid="search-interview-input"
                      />
                      {(() => {
                        const filteredSortedInterviews = interviews
                          .filter((i) => {
                            if (!interviewSearchTerm.trim()) return true;
                            const q = interviewSearchTerm.toLowerCase();
                            return `${i.cognome || ""} ${i.nome || ""} ${i.codice || ""} ${i.telefono || ""}`.toLowerCase().includes(q);
                          })
                          .sort((a, b) => (a.cognome || "").localeCompare(b.cognome || "", "it", { sensitivity: "base" }));
                        return (
                          <Select
                            value={selectedInterview}
                            onValueChange={(v) => { handleImportFromInterview(v); setInterviewSearchTerm(""); }}
                          >
                            <SelectTrigger className="mt-2 bg-white" data-testid="select-interview">
                              <SelectValue placeholder={`Seleziona un'intervista... (${filteredSortedInterviews.length} risultati, ordinati per cognome)`} />
                            </SelectTrigger>
                            <SelectContent className="max-h-72">
                              {filteredSortedInterviews.length === 0 ? (
                                <div className="px-2 py-3 text-sm text-slate-500 text-center">
                                  Nessun cliente trovato
                                </div>
                              ) : (
                                filteredSortedInterviews.map((interview) => (
                                  <SelectItem key={interview.id} value={interview.id}>
                                    {interview.cognome} {interview.nome}{interview.codice ? ` — ${interview.codice}` : ""} — {interview.citta || interview.telefono || "N/D"}
                                  </SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                        );
                      })()}
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Nome *</Label>
                      <Input
                        value={formData.nome}
                        onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                        required
                        data-testid="payment-nome"
                      />
                    </div>
                    <div>
                      <Label>Cognome *</Label>
                      <Input
                        value={formData.cognome}
                        onChange={(e) => setFormData({ ...formData, cognome: e.target.value })}
                        required
                        data-testid="payment-cognome"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Telefono</Label>
                    <Input
                      type="tel"
                      value={formData.telefono}
                      onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                      placeholder="es. 3331234567"
                      data-testid="payment-telefono"
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="es. cliente@email.it"
                      data-testid="payment-email"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Indirizzo</Label>
                      <Input
                        value={formData.indirizzo}
                        onChange={(e) => setFormData({ ...formData, indirizzo: e.target.value })}
                        placeholder="es. Via Roma 123"
                        data-testid="payment-indirizzo"
                      />
                    </div>
                    <div>
                      <Label>Città</Label>
                      <Input
                        value={formData.citta}
                        onChange={(e) => setFormData({ ...formData, citta: e.target.value })}
                        placeholder="es. Cagliari"
                        data-testid="payment-citta"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Piano Consegna</Label>
                    <Input
                      value={formData.piano_consegna}
                      onChange={(e) => setFormData({ ...formData, piano_consegna: e.target.value })}
                      placeholder="es. Piano 3"
                      data-testid="payment-piano"
                    />
                  </div>
                  <div>
                    <Label>Importo Contratto (€) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.importo_contratto}
                      onChange={(e) => setFormData({ ...formData, importo_contratto: e.target.value })}
                      required
                      data-testid="payment-importo"
                    />
                  </div>
                  <div>
                    <Label>Numero Tranche *</Label>
                    <Select
                      value={formData.numero_tranche.toString()}
                      onValueChange={(value) => setFormData({ ...formData, numero_tranche: parseInt(value) })}
                    >
                      <SelectTrigger data-testid="payment-tranche-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 Tranche</SelectItem>
                        <SelectItem value="2">2 Tranche</SelectItem>
                        <SelectItem value="3">3 Tranche</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Note</Label>
                    <Textarea
                      value={formData.note}
                      onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                      rows={2}
                      data-testid="payment-note"
                    />
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                      Annulla
                    </Button>
                    <Button type="submit" className="bg-[#1e40af] hover:bg-[#1e3a8a] text-white" data-testid="submit-payment-btn">
                      {editingPayment ? "Salva" : "Crea"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="stat-card">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-slate-600" />
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.totale_clienti}</p>
                    <p className="text-xs text-slate-500">Clienti</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="stat-card">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <Euro className="w-5 h-5 text-slate-600" />
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{formatCurrency(stats.totale_contratti)}</p>
                    <p className="text-xs text-slate-500">Contratti</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="stat-card">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <TrendingUp className="w-5 h-5 text-emerald-600" />
                  <div>
                    <p className="text-2xl font-bold text-emerald-600">{formatCurrency(stats.totale_incassato)}</p>
                    <p className="text-xs text-slate-500">Incassato</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="stat-card">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <TrendingDown className="w-5 h-5 text-amber-600" />
                  <div>
                    <p className="text-2xl font-bold text-amber-600">{formatCurrency(stats.totale_da_incassare)}</p>
                    <p className="text-xs text-slate-500">Da Incassare</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="stat-card">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-rose-600" />
                  <div>
                    <p className="text-2xl font-bold text-rose-600">{formatCurrency(stats.totale_scaduto)}</p>
                    <p className="text-xs text-slate-500">Scaduto</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Email Templates */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Email Preconfezionate</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Button
                  variant="outline"
                  className="justify-start"
                  onClick={() => copyEmailToClipboard("sollecito_pagamento")}
                  data-testid="email-sollecito-pagamento"
                >
                  <DollarSign className="w-4 h-4 mr-2" />
                  Sollecito pagamento
                </Button>
                
                <Button
                  variant="outline"
                  className="justify-start"
                  onClick={() => copyEmailToClipboard("ultimo_sollecito")}
                  data-testid="email-ultimo-sollecito"
                >
                  <DollarSign className="w-4 h-4 mr-2 text-red-600" />
                  Ultimo sollecito pagamento
                </Button>
                
                <Button
                  variant="outline"
                  className="justify-start"
                  onClick={() => copyEmailToClipboard("richiesta_contatto")}
                  data-testid="email-richiesta-contatto"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Richiesta Contatto
                </Button>
              </div>
              
              {/* Connessione Gmail */}
              <div className="mt-4 pt-4 border-t border-slate-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Mail className="w-5 h-5 text-slate-500" />
                    <span className="text-sm text-slate-600">
                      {gmailConnected ? (
                        <span className="flex items-center gap-1 text-green-600">
                          <CheckCircle2 className="w-4 h-4" />
                          Gmail collegato - Le bozze verranno salvate direttamente
                        </span>
                      ) : (
                        "Collega Gmail per salvare le bozze direttamente nella tua casella"
                      )}
                    </span>
                  </div>
                  {gmailConnected ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDisconnectGmail}
                      className="text-red-600 hover:text-red-700"
                    >
                      Disconnetti
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleConnectGmail}
                      className="border-blue-500 text-blue-600 hover:bg-blue-50"
                    >
                      <Link2 className="w-4 h-4 mr-1" />
                      Collega Gmail
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full max-w-md grid-cols-2 mb-6">
              <TabsTrigger value="tutti">
                Tutti i Clienti ({payments.length})
              </TabsTrigger>
              <TabsTrigger value="morosi" className="text-rose-600 data-[state=active]:text-rose-600">
                Clienti Morosi ({morosi.length})
              </TabsTrigger>
            </TabsList>

            {/* Tab: Tutti i Clienti */}
            <TabsContent value="tutti">
              {/* Search */}
              <div className="mb-6">
                <div className="relative max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Cerca per nome..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="payments-search"
                  />
                </div>
              </div>

              {/* Payments List */}
              {loading ? (
                <SkeletonTable rows={6} cols={4} />
              ) : (
                <div className="space-y-4" data-testid="payments-list">
                  {filteredPayments.map((payment) => {
                    const status = getPaymentStatus(payment);
                    const commessa = findCommessaForClient(payment.nome, payment.cognome);
                    return (
                      <Card key={payment.id} className={status.hasOverdue ? "border-rose-200" : ""} data-testid={`payment-card-${payment.id}`}>
                        <CardContent className="p-0">
                          <Accordion type="single" collapsible>
                            <AccordionItem value="details" className="border-0">
                              <AccordionTrigger className="px-6 py-4 hover:no-underline">
                                <div className="flex items-center justify-between w-full pr-4">
                                  <div className="flex items-center gap-4">
                                    {status.hasOverdue && (
                                      <AlertCircle className="w-5 h-5 text-rose-500" />
                                    )}
                                    <div className="text-left">
                                      <p className="font-semibold text-slate-900">
                                        {payment.nome} {payment.cognome}
                                      </p>
                                      <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                                        {payment.telefono && (
                                          <span 
                                            className="flex items-center gap-1 select-text cursor-text"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <Phone className="w-3 h-3 flex-shrink-0" />
                                            <span className="select-all">{payment.telefono}</span>
                                          </span>
                                        )}
                                        {payment.email && (
                                          <span 
                                            className="flex items-center gap-1 select-text cursor-text"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <Mail className="w-3 h-3 flex-shrink-0" />
                                            <span className="select-all">{payment.email}</span>
                                          </span>
                                        )}
                                      </div>
                                      <p className="text-sm text-slate-500 mt-1">
                                        Contratto: {formatCurrency(payment.importo_contratto)} • {payment.numero_tranche} tranche
                                      </p>
                                      {/* Link alla commessa o avviso */}
                                      {commessa ? (
                                        <a
                                          href={`/commesse-pose/${commessa.id}`}
                                          onClick={(e) => e.stopPropagation()}
                                          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline mt-1"
                                        >
                                          <ExternalLink className="w-3 h-3" />
                                          Vai a Commessa
                                        </a>
                                      ) : (
                                        <span className="inline-flex items-center gap-1 text-xs text-amber-600 mt-1">
                                          <Info className="w-3 h-3" />
                                          Cliente non ancora in Commessa, cercalo nelle Offerte
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-6">
                                    <div className="text-right">
                                      <p className="text-sm text-emerald-600 font-medium">
                                        Pagato: {formatCurrency(status.totalePagato)}
                                      </p>
                                      <p className="text-sm text-rose-600 font-medium">
                                        Da pagare: {formatCurrency(status.totaleDaPagare)}
                                      </p>
                                    </div>
                                    <div className="flex gap-1">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => { e.stopPropagation(); openEditDialog(payment); }}
                                      >
                                        <Edit className="w-4 h-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => { e.stopPropagation(); handleDelete(payment.id); }}
                                        className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              </AccordionTrigger>
                              <AccordionContent className="px-6 pb-6">
                                {payment.note && (
                                  <p className="text-sm text-slate-600 mb-4 p-3 bg-slate-50 rounded">
                                    Note: {payment.note}
                                  </p>
                                )}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                  {[1, 2, 3].slice(0, payment.numero_tranche).map((num) => (
                                    <TrancheCard
                                      key={num}
                                      payment={payment}
                                      trancheNum={num}
                                      onUpdate={handleTrancheUpdate}
                                      onUpload={handleUploadContabile}
                                      gmailConnected={gmailConnected}
                                      setGmailConnected={setGmailConnected}
                                    />
                                  ))}
                                </div>
                              </AccordionContent>
                            </AccordionItem>
                          </Accordion>
                        </CardContent>
                      </Card>
                    );
                  })}
                  {filteredPayments.length === 0 && (
                    <div className="text-center py-12 text-slate-500">
                      {searchTerm ? "Nessun risultato trovato" : "Nessun cliente presente"}
                    </div>
                  )}
                </div>
              )}
            </TabsContent>

            {/* Tab: Clienti Morosi */}
            <TabsContent value="morosi">
              {loading ? (
                <SkeletonTable rows={4} cols={3} />
              ) : morosi.length === 0 ? (
                <Card>
                  <CardContent className="py-12">
                    <div className="text-center text-slate-500">
                      <p className="text-lg font-medium mb-2">🎉 Nessun cliente moroso!</p>
                      <p className="text-sm">Tutti i clienti con pagamenti in sospeso hanno caricato la contabile.</p>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {morosi.map((cliente) => (
                    <Card key={`${cliente.id}-${cliente.tranche_num}`} className="border-rose-200 bg-rose-50/30">
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <AlertCircle className="w-5 h-5 text-rose-600" />
                              <h3 className="font-semibold text-slate-900 text-lg">
                                {cliente.nome} {cliente.cognome}
                              </h3>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                              <div>
                                <p className="text-xs text-slate-500">Email</p>
                                <p className="text-sm font-medium text-slate-900">{cliente.email || "Non disponibile"}</p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-500">Telefono</p>
                                <p className="text-sm font-medium text-slate-900">{cliente.telefono || "Non disponibile"}</p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-500">Importo da Pagare</p>
                                <p className="text-sm font-bold text-rose-600">{formatCurrency(cliente.importo_da_pagare)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-500">Data Scadenza</p>
                                <p className="text-sm font-medium text-slate-900">{cliente.data_scadenza || "Non impostata"}</p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-500">N. Fattura</p>
                                <p className="text-sm font-medium text-slate-900">{cliente.numero_fattura || "Non disponibile"}</p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-500">Tranche</p>
                                <p className="text-sm font-medium text-slate-900">
                                  {cliente.tranche_num} di {cliente.numero_tranche}
                                </p>
                              </div>
                            </div>

                            <div className="mt-4 p-3 bg-rose-100 rounded-lg border border-rose-200">
                              <p className="text-xs font-medium text-rose-800">
                                ⚠️ Pagamento non effettuato e contabile non caricata
                              </p>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
    </PageLayout>
  );
}

// Helper per convertire data italiana (gg-mm-aaaa) in Date object
const parseItalianDate = (dateStr) => {
  if (!dateStr) return null;
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    return new Date(parts[2], parts[1] - 1, parts[0]);
  }
  return new Date(dateStr);
};

// Helper per formattare input data italiana
const formatDateInput = (val) => {
  let cleaned = val.replace(/[^\d-]/g, '');
  if (cleaned.length === 2 && !cleaned.includes('-')) cleaned += '-';
  if (cleaned.length === 5 && cleaned.split('-').length === 2) cleaned += '-';
  return cleaned.slice(0, 10);
};

// Data di oggi nel formato gg-mm-aaaa
const todayDDMMYYYY = () => {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}-${mm}-${d.getFullYear()}`;
};

function TrancheCard({ payment, trancheNum, onUpdate, onUpload, gmailConnected, setGmailConnected }) {
  const tranche = payment[`tranche_${trancheNum}`] || {};
  const [isUploading, setIsUploading] = useState(false);
  // Fattura PDF allegata alla bozza mail (solo in memoria, resettata al riavvio pagina)
  const [fatturaPdf, setFatturaPdf] = useState(null); // { name, base64, mime }
  const [localData, setLocalData] = useState({
    numero_fattura: tranche.numero_fattura || "",
    data_fattura: tranche.data_fattura || "",
    importo_ivato: tranche.importo_ivato || 0,
    data_invio: tranche.data_invio || "",
    data_scadenza: tranche.data_scadenza || "",
    pagata: tranche.pagata || false,
    // Ignora il valore "uploading" se presente nel database
    contabile_path: (tranche.contabile_path && tranche.contabile_path !== "uploading") ? tranche.contabile_path : null,
    contabile_available: tranche.contabile_available !== false, // true se non specificato o se true
    mail_dichiarazione_inviata: tranche.mail_dichiarazione_inviata || false,
    data_mail_dichiarazione: tranche.data_mail_dichiarazione || ""
  });

  // Sincronizza localData quando payment cambia (es. dopo il salvataggio)
  useEffect(() => {
    const updatedTranche = payment[`tranche_${trancheNum}`] || {};
    setLocalData({
      numero_fattura: updatedTranche.numero_fattura || "",
      data_fattura: updatedTranche.data_fattura || "",
      importo_ivato: updatedTranche.importo_ivato || 0,
      data_invio: updatedTranche.data_invio || "",
      data_scadenza: updatedTranche.data_scadenza || "",
      pagata: updatedTranche.pagata || false,
      // Ignora il valore "uploading" se presente nel database
      contabile_path: (updatedTranche.contabile_path && updatedTranche.contabile_path !== "uploading") ? updatedTranche.contabile_path : null,
      contabile_available: updatedTranche.contabile_available !== false,
      mail_dichiarazione_inviata: updatedTranche.mail_dichiarazione_inviata || false,
      data_mail_dichiarazione: updatedTranche.data_mail_dichiarazione || ""
    });
    setIsUploading(false);
  }, [payment, trancheNum]);

  const isOverdue = localData.data_scadenza && !localData.pagata && parseItalianDate(localData.data_scadenza) < new Date();

  const handleSave = () => {
    if (localData.pagata && !localData.contabile_path) {
      toast.error("Carica la contabile prima di spuntare come pagata");
      return;
    }
    if (localData.mail_dichiarazione_inviata && !localData.data_mail_dichiarazione) {
      toast.error("Indica la data di invio mail prima di spuntare come inviata");
      return;
    }
    onUpdate(payment.id, trancheNum, localData);
  };

  const handleCreaBozzaMail = async () => {
    // Blocca se non è stata caricata la Fattura PDF
    if (!fatturaPdf) {
      toast.error(
        "Devi prima allegare la Fattura PDF! Clicca su 'Allega Fattura PDF' per selezionare il file, poi riprova.",
        { duration: 6000 }
      );
      return;
    }
    // Determina il testo per [x] in base al numero di tranche e alla tranche corrente
    let tipoTrancheOggetto = "";
    let tipoTrancheTesto = "";
    
    if (payment.numero_tranche === 1) {
      tipoTrancheOggetto = "Unica";
      tipoTrancheTesto = "Unica";
    } else if (payment.numero_tranche === 2) {
      tipoTrancheOggetto = trancheNum === 1 ? "Prima" : "Seconda";
      tipoTrancheTesto = trancheNum === 1 ? "Prima" : "Seconda";
    } else if (payment.numero_tranche === 3) {
      if (trancheNum === 1) {
        tipoTrancheOggetto = "Prima";
        tipoTrancheTesto = "Prima";
      } else if (trancheNum === 2) {
        tipoTrancheOggetto = "Seconda";
        tipoTrancheTesto = "Seconda";
      } else {
        tipoTrancheOggetto = "Terza";
        tipoTrancheTesto = "Terza";
      }
    }
    
    const oggetto = `Consegna ${tipoTrancheOggetto} Dichiarazione di Incasso`;
    
    const numeroFattura = localData.numero_fattura || "[XX]";
    const dataFattura = localData.data_fattura || "[XX/XX/XXXX]";
    const importo = (localData.importo_ivato !== null && localData.importo_ivato !== undefined && localData.importo_ivato !== 0)
      ? localData.importo_ivato.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) 
      : "[XXXXXX,XX]";
    
    // Determina se usare il testo della prima tranche o delle successive
    const isPrimaTrancheOUnica = (payment.numero_tranche === 1) || (payment.numero_tranche > 1 && trancheNum === 1);
    const templateKey = isPrimaTrancheOUnica ? "fattura_prima_tranche" : "fattura_altre_tranche";
    
    // Carica template dal backend (Impostazioni → Modelli Email)
    let testoHtml;
    let testoPlain;
    let subjectFromTemplate = oggetto;
    try {
      const tplRes = await axios.get(`${API}/mail-templates/${templateKey}`, { withCredentials: true });
      const tpl = tplRes.data;
      const ctx = {
        tipo_tranche: tipoTrancheTesto,
        numero_fattura: numeroFattura,
        data_fattura: dataFattura,
        importo: importo,
      };
      // Sostituzione placeholder {{key}}
      const applyPlaceholders = (s) => (s || "").replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, k) => (ctx[k] != null ? String(ctx[k]) : ""));
      subjectFromTemplate = applyPlaceholders(tpl.subject) || oggetto;
      const introHtml = applyPlaceholders(tpl.intro);
      const outroHtml = applyPlaceholders(tpl.outro);
      const signatureBlockHtml = `<p><br><strong>${tpl.signature_name || ""}</strong><br>${tpl.signature_role || ""}</p>`;
      testoHtml = `${introHtml}${outroHtml}${signatureBlockHtml}`;
      // Plain fallback (deriva dall'HTML)
      const stripHtml = (s) => s.replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/\n{3,}/g, "\n\n").trim();
      testoPlain = stripHtml(`${introHtml}\n${outroHtml}\n\n${tpl.signature_name}\n${tpl.signature_role}`);
    } catch (tplErr) {
      console.error("Errore caricamento template fattura, uso fallback:", tplErr);
      toast.error("Impossibile caricare il modello email. Uso fallback base.");
      testoHtml = `<p>Gentilissimo Cliente,</p><p>Le comunichiamo la <strong>${tipoTrancheTesto}</strong> Dichiarazione di Incasso.</p><p><strong>Causale:</strong> Fattura numero ${numeroFattura} del ${dataFattura}.<br><strong>Importo:</strong> ${importo} Euro</p>`;
      testoPlain = `Gentilissimo Cliente,\n\nLe comunichiamo la ${tipoTrancheTesto} Dichiarazione di Incasso.\nCausale: Fattura ${numeroFattura} del ${dataFattura}.\nImporto: ${importo} Euro`;
    }
    

    // Se Gmail è connesso, crea la bozza in Gmail
    if (gmailConnected && payment.email) {
      try {
        await axios.post(`${API}/gmail/create-draft`, {
          to_email: payment.email,
          subject: subjectFromTemplate,
          body_html: testoHtml,
          payment_id: payment.id,
          tranche_num: trancheNum,
          attachment_base64: fatturaPdf.base64,
          attachment_filename: fatturaPdf.name,
          attachment_mime: fatturaPdf.mime,
        }, { withCredentials: true });
        toast.success(`Bozza creata in Gmail con allegato "${fatturaPdf.name}". Vai nelle tue bozze per inviarla.`);
      } catch (e) {
        console.error("Error creating Gmail draft:", e);
        if (e.response?.status === 401) {
          // 401 può essere transitorio (token appena scaduto). Verifica lo stato
          // reale prima di dichiarare Gmail disconnesso.
          try {
            const st = await axios.get(`${API}/oauth/gmail/status`, { withCredentials: true });
            if (st.data?.connected) {
              // Il backend ha rifreshato il token: riprova UNA volta
              try {
                await axios.post(`${API}/gmail/create-draft`, {
                  to_email: payment.email,
                  subject: subjectFromTemplate,
                  body_html: testoHtml,
                  payment_id: payment.id,
                  tranche_num: trancheNum,
                  attachment_base64: fatturaPdf.base64,
                  attachment_filename: fatturaPdf.name,
                  attachment_mime: fatturaPdf.mime,
                }, { withCredentials: true });
                toast.success(`Bozza creata in Gmail con allegato "${fatturaPdf.name}". Vai nelle tue bozze per inviarla.`);
                return;
              } catch (retryErr) {
                console.error("Retry create-draft failed:", retryErr);
              }
            }
            // Se lo stato dice disconnesso davvero, aggiorna UI
            setGmailConnected(false);
            toast.error("Gmail non autorizzato. Riconnetti Gmail.");
          } catch (_) {
            setGmailConnected(false);
            toast.error("Gmail non autorizzato. Riconnetti Gmail.");
          }
        } else {
          toast.error("Errore nella creazione della bozza Gmail");
        }
        // Fallback: copia negli appunti (allegato NON incluso)
        navigator.clipboard.writeText(`Oggetto: ${subjectFromTemplate}\n\n${testoPlain}`)
          .then(() => toast.info(`Mail copiata negli appunti. ATTENZIONE: la Fattura "${fatturaPdf.name}" NON è stata allegata perché Gmail non è disponibile. Allegala manualmente all'email prima di inviarla.`, { duration: 8000 }))
          .catch(() => toast.error("Impossibile copiare negli appunti. Autorizza il browser."));
      }
    } else {
      // Fallback: copia negli appunti (allegato NON incluso perché Gmail non è collegato)
      navigator.clipboard.writeText(`Oggetto: ${subjectFromTemplate}\n\n${testoPlain}`)
        .then(() => {
          if (!gmailConnected) {
            toast.warning(`Mail copiata negli appunti. ATTENZIONE: la Fattura "${fatturaPdf.name}" NON è stata allegata (Gmail non collegato). Allegala manualmente prima di inviarla, oppure collega Gmail per salvare la bozza con allegato.`, { duration: 8000 });
          } else if (!payment.email) {
            toast.warning(`Mail copiata negli appunti (email cliente non presente). La Fattura "${fatturaPdf.name}" NON è stata allegata: allegala manualmente.`, { duration: 8000 });
          } else {
            toast.success("Mail copiata negli appunti!");
          }
        })
        .catch(() => toast.error("Impossibile copiare negli appunti. Autorizza il browser."));
    }
  };

  return (
    <div className={`border rounded-lg p-4 ${isOverdue ? "border-rose-300 bg-rose-50" : "border-slate-200"}`}>
      <h4 className="font-semibold text-slate-900 mb-3">Tranche {trancheNum}</h4>
      <div className="space-y-3">
        <div>
          <Label className="text-xs">N. Fattura</Label>
          <Input
            value={localData.numero_fattura}
            onChange={(e) => setLocalData({ ...localData, numero_fattura: e.target.value })}
            className="h-8 text-sm"
            data-testid={`tranche-${trancheNum}-fattura`}
          />
        </div>
        <div>
          <Label className="text-xs">Data Fattura (gg-mm-aaaa)</Label>
          <div className="flex items-center gap-1">
            <Input
              type="text"
              placeholder="es. 15-01-2026"
              value={localData.data_fattura}
              onChange={(e) => setLocalData({ ...localData, data_fattura: formatDateInput(e.target.value) })}
              className="h-8 text-sm"
              data-testid={`tranche-${trancheNum}-data-fattura`}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8 px-2 text-xs whitespace-nowrap"
              onClick={() => setLocalData({ ...localData, data_fattura: todayDDMMYYYY() })}
              data-testid={`tranche-${trancheNum}-data-fattura-oggi`}
              title="Imposta la data di oggi"
            >
              Oggi
            </Button>
          </div>
        </div>
        <div>
          <Label className="text-xs">Importo IVA incl. (€)</Label>
          <Input
            type="number"
            step="0.01"
            value={localData.importo_ivato}
            onChange={(e) => setLocalData({ ...localData, importo_ivato: parseFloat(e.target.value) || 0 })}
            className="h-8 text-sm"
            data-testid={`tranche-${trancheNum}-importo`}
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Data Invio (gg-mm-aaaa)</Label>
            <div className="flex items-center gap-1">
              <Input
                type="text"
                placeholder="es. 04-12-2025"
                value={localData.data_invio}
                onChange={(e) => setLocalData({ ...localData, data_invio: formatDateInput(e.target.value) })}
                className="h-8 text-sm"
                data-testid={`tranche-${trancheNum}-invio`}
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8 px-2 text-xs whitespace-nowrap"
                onClick={() => setLocalData({ ...localData, data_invio: todayDDMMYYYY() })}
                data-testid={`tranche-${trancheNum}-data-invio-oggi`}
                title="Imposta la data di oggi"
              >
                Oggi
              </Button>
            </div>
          </div>
          <div>
            <Label className="text-xs">Scadenza (gg-mm-aaaa)</Label>
            <div className="flex items-center gap-1">
              <Input
                type="text"
                placeholder="es. 04-01-2026"
                value={localData.data_scadenza}
                onChange={(e) => setLocalData({ ...localData, data_scadenza: formatDateInput(e.target.value) })}
                className="h-8 text-sm"
                data-testid={`tranche-${trancheNum}-scadenza`}
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8 px-2 text-xs whitespace-nowrap"
                onClick={() => setLocalData({ ...localData, data_scadenza: todayDDMMYYYY() })}
                data-testid={`tranche-${trancheNum}-data-scadenza-oggi`}
                title="Imposta la data di oggi"
              >
                Oggi
              </Button>
            </div>
          </div>
        </div>
        <div>
          <Label className="text-xs">Contabile</Label>
          {localData.contabile_path ? (
            localData.contabile_available ? (
              <div className="flex items-center gap-2">
                <a
                  href={`${API}/files/${localData.contabile_path}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-sm text-blue-600 hover:underline"
                >
                  <FileText className="w-4 h-4" />
                  Visualizza
                </a>
                <button
                  onClick={async () => {
                    if (window.confirm("Sei sicuro di voler eliminare questa contabile?")) {
                      try {
                        await axios.delete(`${API}/payments/${payment.id}/contabile/${trancheNum}`, { withCredentials: true });
                        setLocalData({ ...localData, contabile_path: null, contabile_available: false });
                        toast.success("Contabile eliminata");
                      } catch (e) {
                        console.error("Error deleting contabile:", e);
                        toast.error("Errore nell'eliminazione della contabile");
                      }
                    }
                  }}
                  className="text-red-500 hover:text-red-700 text-xs flex items-center gap-1 bg-transparent"
                >
                  <Trash2 className="w-3 h-3" />
                  Elimina
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-1 text-amber-600 text-xs">
                  <AlertTriangle className="w-3 h-3" />
                  File non disponibile - ricarica
                </div>
                <div className="flex items-center gap-2">
                  <FileDropZone
                    accept=".pdf,.jpg,.jpeg,.png"
                    disabled={isUploading}
                    dropLabel="Rilascia qui la contabile"
                    className="inline-block"
                    onFile={async (file) => {
                      setIsUploading(true);
                      try {
                        const formData = new FormData();
                        formData.append("file", file);
                        const response = await axios.post(`${API}/payments/${payment.id}/upload-contabile/${trancheNum}`, formData, {
                          withCredentials: true,
                          headers: { "Content-Type": "multipart/form-data" }
                        });
                        setLocalData({ ...localData, contabile_path: response.data.file_path, contabile_available: true });
                        toast.success("Contabile caricata con successo");
                      } catch (err) {
                        console.error("Error uploading contabile:", err);
                        toast.error("Errore nel caricamento della contabile");
                      } finally {
                        setIsUploading(false);
                      }
                    }}
                  >
                    <label className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 cursor-pointer">
                      <Upload className="w-4 h-4" />
                      {isUploading ? "Caricamento..." : "Ricarica"}
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        className="hidden"
                        disabled={isUploading}
                        onChange={async (e) => {
                          if (e.target.files[0]) {
                            setIsUploading(true);
                            try {
                              const formData = new FormData();
                              formData.append("file", e.target.files[0]);
                              const response = await axios.post(`${API}/payments/${payment.id}/upload-contabile/${trancheNum}`, formData, {
                                withCredentials: true,
                                headers: { "Content-Type": "multipart/form-data" }
                              });
                              setLocalData({ ...localData, contabile_path: response.data.file_path, contabile_available: true });
                              toast.success("Contabile caricata con successo");
                            } catch (err) {
                              console.error("Error uploading contabile:", err);
                              toast.error("Errore nel caricamento della contabile");
                            } finally {
                              setIsUploading(false);
                            }
                          }
                        }}
                      />
                    </label>
                  </FileDropZone>
                  <button
                    onClick={async () => {
                      try {
                        await axios.delete(`${API}/payments/${payment.id}/contabile/${trancheNum}`, { withCredentials: true });
                        setLocalData({ ...localData, contabile_path: null, contabile_available: false });
                        toast.success("Riferimento eliminato");
                      } catch (e) {
                        console.error("Error deleting contabile:", e);
                        toast.error("Errore nell'eliminazione");
                      }
                    }}
                    className="text-red-500 hover:text-red-700 text-xs flex items-center gap-1 bg-transparent"
                  >
                    <Trash2 className="w-3 h-3" />
                    Elimina
                  </button>
                </div>
              </div>
            )
          ) : (
            <FileDropZone
              accept=".pdf,.jpg,.jpeg,.png"
              disabled={isUploading}
              onFile={async (file) => {
                setIsUploading(true);
                try {
                  await onUpload(payment.id, trancheNum, file);
                } catch (err) {
                  console.error("Upload error:", err);
                  toast.error("Errore nel caricamento");
                } finally {
                  setIsUploading(false);
                }
              }}
              dropLabel="Rilascia qui la contabile"
              className="inline-block"
            >
              <label
                className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 cursor-pointer"
                data-testid={`tranche-${trancheNum}-upload-contabile-label`}
              >
                <Upload className="w-4 h-4" />
                {isUploading ? "Caricamento..." : "Carica"}
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="hidden"
                  data-testid={`tranche-${trancheNum}-upload-contabile-input`}
                  disabled={isUploading}
                  onChange={async (e) => {
                    if (e.target.files[0]) {
                      setIsUploading(true);
                      try {
                        await onUpload(payment.id, trancheNum, e.target.files[0]);
                      } catch (err) {
                        console.error("Upload error:", err);
                        toast.error("Errore nel caricamento");
                      } finally {
                        setIsUploading(false);
                      }
                    }
                  }}
                />
              </label>
            </FileDropZone>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id={`pagata-${payment.id}-${trancheNum}`}
            checked={localData.pagata}
            onCheckedChange={(checked) => setLocalData({ ...localData, pagata: checked })}
            data-testid={`tranche-${trancheNum}-pagata`}
          />
          <Label htmlFor={`pagata-${payment.id}-${trancheNum}`} className="text-sm">
            Pagata
          </Label>
        </div>
        <Button
          size="sm"
          onClick={handleSave}
          className="w-full"
          data-testid={`tranche-${trancheNum}-save`}
        >
          Salva
        </Button>

        {/* Sezione Dichiarazione di Incasso */}
        <div className="border-t border-slate-200 pt-3 mt-3">
          {/* Allega Fattura PDF (obbligatorio per Crea Bozza) */}
          <FileDropZone
            accept="application/pdf,.pdf"
            dropLabel="Rilascia la Fattura PDF"
            className="block mb-2"
            onFile={(file) => {
              if (file.type !== "application/pdf") {
                toast.error(`"${file.name}": formato non ammesso. Solo PDF.`);
                return;
              }
              if (file.size > 25 * 1024 * 1024) {
                toast.error(`"${file.name}" supera 25 MB (limite Gmail).`);
                return;
              }
              const reader = new FileReader();
              reader.onload = () => {
                const b64 = String(reader.result).split(",")[1] || "";
                setFatturaPdf({ name: file.name, base64: b64, mime: file.type });
                toast.success(`Fattura "${file.name}" pronta per essere allegata`);
              };
              reader.readAsDataURL(file);
            }}
          >
            <label
              className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium border cursor-pointer transition-colors ${
                fatturaPdf
                  ? "bg-emerald-50 border-emerald-400 text-emerald-800 hover:bg-emerald-100"
                  : "bg-transparent border-slate-300 text-slate-700 hover:bg-slate-100"
              }`}
              data-testid={`tranche-${trancheNum}-allega-fattura-label`}
            >
              <input
                type="file"
                accept="application/pdf"
                className="hidden"
                data-testid={`tranche-${trancheNum}-allega-fattura-input`}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (file.type !== "application/pdf") {
                    toast.error(`"${file.name}": formato non ammesso. Solo PDF.`);
                    e.target.value = "";
                    return;
                  }
                  if (file.size > 25 * 1024 * 1024) {
                    toast.error(`"${file.name}" supera 25 MB (limite Gmail).`);
                    e.target.value = "";
                    return;
                  }
                  const reader = new FileReader();
                  reader.onload = () => {
                    const b64 = String(reader.result).split(",")[1] || "";
                    setFatturaPdf({ name: file.name, base64: b64, mime: file.type });
                    toast.success(`Fattura "${file.name}" pronta per essere allegata`);
                  };
                  reader.readAsDataURL(file);
                }}
              />
              {fatturaPdf ? <CheckCircle2 className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
              {fatturaPdf ? `${fatturaPdf.name}` : "Allega Fattura PDF"}
            </label>
          </FileDropZone>
          {fatturaPdf && (
            <button
              type="button"
              onClick={() => setFatturaPdf(null)}
              className="text-xs text-red-600 hover:text-red-700 underline mb-2 block"
              data-testid={`tranche-${trancheNum}-rimuovi-fattura`}
            >
              Rimuovi allegato
            </button>
          )}

          <Button
            size="sm"
            variant="outline"
            onClick={handleCreaBozzaMail}
            className="w-full bg-transparent text-slate-700 border-slate-300 hover:bg-slate-100"
            data-testid={`tranche-${trancheNum}-crea-bozza-mail`}
          >
            <Mail className="w-4 h-4 mr-2" />
            Crea Bozza Mail
          </Button>
          
          <p className="text-xs text-amber-600 mt-3 mb-2 font-semibold flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            Inviare mail al cliente
            <AlertCircle className="w-4 h-4" />
          </p>
          
          <div className="flex items-center gap-2 mb-2">
            <Checkbox
              id={`mail-inviata-${payment.id}-${trancheNum}`}
              checked={localData.mail_dichiarazione_inviata}
              onCheckedChange={(checked) => setLocalData({ ...localData, mail_dichiarazione_inviata: checked })}
              data-testid={`tranche-${trancheNum}-mail-inviata`}
            />
            <Label htmlFor={`mail-inviata-${payment.id}-${trancheNum}`} className="text-sm">
              Mail Inviata
            </Label>
          </div>
          
          <div>
            <Label className="text-xs">Data Invio Mail (gg-mm-aaaa)</Label>
            <div className="flex items-center gap-1">
              <Input
                type="text"
                placeholder="es. 04-12-2025"
                value={localData.data_mail_dichiarazione}
                onChange={(e) => setLocalData({ ...localData, data_mail_dichiarazione: formatDateInput(e.target.value) })}
                className="h-8 text-sm"
                data-testid={`tranche-${trancheNum}-data-mail`}
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8 px-2 text-xs whitespace-nowrap"
                onClick={() => setLocalData({ ...localData, data_mail_dichiarazione: todayDDMMYYYY() })}
                data-testid={`tranche-${trancheNum}-data-mail-oggi`}
                title="Imposta la data di oggi"
              >
                Oggi
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
